import React, { useState } from "react";
import {
  Modal,
  Container,
  Title,
  Text,
  Stack,
  Button,
  Group,
  ActionIcon,
  Card,
  Alert,
  Divider,
  ScrollArea,
} from "@mantine/core";
import {
  IconX,
  IconCreditCard,
  IconShield,
  IconCheck,
  IconBrandStripe,
  IconBrandFlutter,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { PlanProps } from "../../Lib/types";
import { Colors, FlutterwaveLogoSmall, PaystackLogo } from "../../Lib/data";

interface PaymentMethodSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  planDetails?: PlanProps;
  customerInfo?: {
    name: string;
    email: string;
    phone?: string;
  };
  onStripeSelected?: () => void;
  onFlutterwaveSelected?: () => void;
  onPaystackSelected?: () => void;
}

const PaymentMethodSelectionModal: React.FC<PaymentMethodSelectionModalProps> = ({
  opened,
  onClose,
  planDetails,
  customerInfo,
  onStripeSelected,
  onFlutterwaveSelected,
  onPaystackSelected,
}) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<
    "stripe" | "flutterwave" | "paystack" | null
  >(null);

  const handleMethodSelect = (method: "stripe" | "flutterwave" | "paystack") => {
    setSelectedMethod(method);
  };

  const handleContinue = () => {
    if (selectedMethod === "stripe") {
      onStripeSelected?.();
    } else if (selectedMethod === "flutterwave") {
      onFlutterwaveSelected?.();
    } else if (selectedMethod === "paystack") {
      onPaystackSelected?.();
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedMethod(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      centered
      withCloseButton={false}
      className="payment-method-selection-modal"
      overlayProps={{
        blur: 2,
        opacity: 0.6,
      }}
    >
      <Container size="sm" py="xl">
        <ScrollArea h={370}>
          {/* Header */}
          <Group justify="space-between" align="center" mb="xl">
            <div>
              <Title order={3}>{t("plans.choosePaymentMethod")}</Title>
              <Text c="dimmed" size="sm">
                {t("plans.selectPaymentProvider")}
              </Text>
            </div>
            <ActionIcon variant="subtle" size="lg" onClick={handleClose} className="close-button">
              <IconX size={20} />
            </ActionIcon>
          </Group>

          {/* Payment Methods */}
          <Stack gap="md" mb="xl">
            <Title order={4} mb="md">
              {t("plans.choosePaymentMethod")}
            </Title>

            {/* Payment Options */}
            <div className="payment-methods-grid">
              {/* <div className="display-none"> */}
              {/* Stripe Option */}
              {/* <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  shadow="sm"
                  p="lg"
                  radius="md"
                  withBorder
                  className={`payment-method-card ${selectedMethod === "stripe" ? "selected" : ""}`}
                  onClick={() => handleMethodSelect("stripe")}
                  style={{ cursor: "pointer" }}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="md">
                      <IconBrandStripe size={32} color="#6772e5" />
                      <div>
                        <Text fw={600} size="lg">
                          {t("plans.stripe")}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t("plans.stripeDescription")}
                        </Text>
                      </div>
                    </Group>
                    {selectedMethod === "stripe" && (
                      <IconCheck size={20} color={Colors.GreenPrimary} />
                    )}
                  </Group>
                </Card>
              </motion.div> */}
              {/* Paystack Option */}
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  shadow="sm"
                  p="lg"
                  radius="md"
                  withBorder
                  className={`payment-method-card ${
                    selectedMethod === "paystack" ? "selected" : ""
                  }`}
                  onClick={() => handleMethodSelect("paystack")}
                  style={{ cursor: "pointer" }}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="md">
                      <img src={PaystackLogo} className="payment-logo" alt={t("plans.paystack")} />
                      <div>
                        <Text fw={600} size="lg">
                          {t("plans.paystack")}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t("plans.paystackDescription")}
                        </Text>
                      </div>
                    </Group>
                    {selectedMethod === "paystack" && (
                      <IconCheck size={20} color={Colors.GreenPrimary} />
                    )}
                  </Group>
                </Card>
              </motion.div>
              {/* </div> */}
              {/* Flutterwave Option */}
              {/* <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  shadow="sm"
                  p="lg"
                  radius="md"
                  withBorder
                  className={`payment-method-card ${
                    selectedMethod === "flutterwave" ? "selected" : ""
                  }`}
                  onClick={() => handleMethodSelect("flutterwave")}
                  style={{ cursor: "pointer" }}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="md">
                      <img src={FlutterwaveLogoSmall} className="payment-logo" alt="" />
                      <div>
                        <Text fw={600} size="lg">
                          {t("plans.flutterwave")}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t("plans.flutterwaveDescription")}
                        </Text>
                      </div>
                    </Group>
                    {selectedMethod === "flutterwave" && (
                      <IconCheck size={20} color={Colors.GreenPrimary} />
                    )}
                  </Group>
                </Card>
              </motion.div> */}
            </div>
          </Stack>
        </ScrollArea>

        {/* Continue Button */}
        <Button
          fullWidth
          size="lg"
          disabled={!selectedMethod}
          onClick={handleContinue}
          className="continue-button"
          c={Colors.White}
        >
          {selectedMethod === "stripe"
            ? t("plans.continueWithStripe")
            : selectedMethod === "flutterwave"
            ? t("plans.continueWithFlutterwave")
            : selectedMethod === "paystack"
            ? t("plans.continueWithPaystack")
            : t("plans.continueWithPayment")}
        </Button>
      </Container>
    </Modal>
  );
};

export default PaymentMethodSelectionModal;
